import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Receipt, Search, User, Plus, Copy, QrCode, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, CheckCircle2, Clock, XCircle, Loader2, Lock, Repeat, AlertTriangle, Send, Pencil, Ban,
} from "lucide-react";

const PAGE_SIZE = 25;

const STATUS_BADGE = {
  ATIVA: { label: "Em aberto", className: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  PAGA: { label: "Paga", className: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  ERRO: { label: "Erro ao gerar", className: "bg-red-500/10 text-red-400 border-red-500/30" },
  CANCELADA: { label: "Cancelada", className: "bg-slate-800 text-slate-500 border-slate-700" },
};

const OVERDUE_BADGE = { label: "Vencida", className: "bg-red-500/10 text-red-400 border-red-500/30" };

const EMPTY_FILTERS = { status: "", provider: "", blocking: "", due_from: "", due_to: "" };

// ordem = ordem em que os lembretes saem; so aparecem os que ja foram enviados
const REMINDER_LABELS = [
  ["billing_before_5", "5d antes"], ["billing_on_due", "no dia"], ["billing_after_5", "+5d"],
  ["billing_after_10", "+10d"], ["billing_after_15", "+15d"], ["billing_after_30", "+30d"],
  ["billing_after_45", "+45d"],
];

const plural = (n, um, varios) => (n === 1 ? um : varios);

const inputCls = "bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500/40 focus-visible:border-emerald-500";

function fmtMoney(v) {
  const n = parseFloat(v || 0);
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso.length === 10 ? iso + "T00:00:00" : iso).toLocaleDateString("pt-BR");
  } catch {
    return iso;
  }
}

function fmtPct(v) {
  return Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 2 }) + "%";
}

function dueHint(c) {
  if (c.status !== "ATIVA" || c.days_diff == null) return null;
  const d = c.days_diff;
  if (d < 0) return { text: `vencida há ${-d} ${plural(-d, "dia", "dias")}`, className: "text-red-400" };
  if (d === 0) return { text: "vence hoje", className: "text-amber-400" };
  return { text: `vence em ${d} ${plural(d, "dia", "dias")}`, className: d <= 3 ? "text-amber-400" : "text-slate-500" };
}

function chargeExtras(c) {
  const out = [];
  if (c.discount_type && c.discount_value) {
    out.push(c.discount_type === "percent" ? `Desc. ${fmtPct(c.discount_value)}` : `Desc. ${fmtMoney(c.discount_value)}`);
  }
  if (c.fine_pct) out.push(`Multa ${fmtPct(c.fine_pct)}`);
  if (c.interest_pct) out.push(`Juros ${fmtPct(c.interest_pct)}/mês`);
  return out;
}

function SummaryCard({ testId, label, value, sub, tone = "slate", icon: Icon, onClick }) {
  const tones = { amber: "text-amber-400", red: "text-red-400", emerald: "text-emerald-400", sky: "text-sky-400", slate: "text-slate-300" };
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      data-testid={testId}
      onClick={onClick}
      className={`text-left rounded-lg border border-slate-800 bg-slate-900/50 p-4 ${onClick ? "hover:border-slate-600 transition-colors cursor-pointer" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
        {Icon && <Icon className={`w-4 h-4 ${tones[tone]}`} />}
      </div>
      <div className={`font-mono-jb text-xl font-semibold mt-2 ${tones[tone]}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
    </Wrapper>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1 block">{label}</label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${inputCls} h-9 text-sm rounded-md border px-2`}
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function ClientPicker({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim() || value) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/clients", { params: { search: query, page: 1, limit: 8, include_inactive: true } });
        setResults(data.clients || data.items || []);
        setOpen(true);
      } catch {
        // busca falhou silenciosamente - usuário pode tentar de novo
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, value]);

  if (value) {
    return (
      <div className="flex items-center justify-between rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2.5">
        <div>
          <div className="text-sm text-white font-medium">
            {value.nome}
            {value.cli_ativado !== "s" && (
              <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-400">Desativado</span>
            )}
          </div>
          <div className="text-xs font-mono-jb text-slate-500">{value.login}</div>
        </div>
        <Button
          type="button" size="sm" variant="outline"
          data-testid="client-picker-clear"
          onClick={() => onChange(null)}
          className="border-slate-700 hover:bg-slate-800 hover:text-white text-xs"
        >
          Trocar
        </Button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          data-testid="client-picker-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Buscar cliente por nome ou login…"
          className={`${inputCls} pl-9`}
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-800 bg-slate-900 shadow-xl max-h-64 overflow-y-auto themed-scrollbar">
          {results.map((c) => (
            <button
              key={c.login}
              type="button"
              data-testid={`client-picker-option-${c.login}`}
              onClick={() => { onChange(c); setOpen(false); setQuery(""); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-800 transition-colors"
            >
              <User className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm text-white truncate">
                  {c.nome}
                  {c.cli_ativado !== "s" && (
                    <span data-testid={`client-picker-inactive-${c.login}`} className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-400">Desativado</span>
                  )}
                </div>
                <div className="text-xs font-mono-jb text-slate-500">{c.login}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CancelRecurrenceDialog({ state, onClose, onConfirm }) {
  if (!state) return null;
  const { recurrence, openCharges } = state;
  return (
    <Dialog open={!!state} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-400" /> Cancelar recorrência
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-300">
            Cancelar a recorrência de <span className="text-white font-medium">{recurrence.client_name}</span>{" "}
            ({fmtMoney(recurrence.amount)} todo dia {recurrence.day_of_month}) impede novas cobranças futuras.
          </p>

          {openCharges.length > 0 ? (
            <>
              <p className="text-sm text-amber-300">
                Essa recorrência já gerou {openCharges.length === 1 ? "a cobrança" : `${openCharges.length} cobranças`} abaixo,
                ainda em aberto. Quer cancelar {openCharges.length === 1 ? "ela" : "elas"} também?
              </p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto themed-scrollbar">
                {openCharges.map((c) => (
                  <div key={c._id} className="flex items-center justify-between text-xs rounded-md border border-slate-800 bg-slate-950 px-3 py-2">
                    <span className="text-slate-300">{c.description || "Cobrança avulsa"}</span>
                    <span className="font-mono-jb text-slate-400">{fmtMoney(c.amount)} · vence {fmtDate(c.due_date)}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  type="button" data-testid="cancel-recurrence-only"
                  onClick={() => onConfirm(false)}
                  className="flex-1 bg-slate-800 hover:bg-slate-700 text-white"
                >
                  Só a recorrência
                </Button>
                <Button
                  type="button" data-testid="cancel-recurrence-and-charges"
                  onClick={() => onConfirm(true)}
                  className="flex-1 bg-red-500/90 hover:bg-red-500 text-white"
                >
                  Recorrência + cobrança(s)
                </Button>
              </div>
            </>
          ) : (
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-slate-700 hover:bg-slate-800 text-white">
                Voltar
              </Button>
              <Button
                type="button" data-testid="cancel-recurrence-confirm"
                onClick={() => onConfirm(false)}
                className="flex-1 bg-red-500/90 hover:bg-red-500 text-white"
              >
                Confirmar cancelamento
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResultDialog({ charge, onClose, onSend, sending }) {
  if (!charge) return null;
  return (
    <Dialog open={!!charge} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" /> Cobrança criada
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 min-w-0">
          <div className="text-sm text-slate-300">
            PIX gerado para <span className="text-white font-medium">{charge.client_name}</span> via{" "}
            <Badge className="bg-slate-800 text-slate-300 border-slate-700 uppercase text-[10px]">{charge.provider}</Badge>
            {charge.has_boleto && (
              <Badge className="bg-sky-500/10 text-sky-300 border-sky-500/30 uppercase text-[10px]">+ boleto</Badge>
            )}
          </div>
          {charge.boleto_digitable && (
            <div className="text-xs text-slate-400 break-all" data-testid="avulsa-result-digitable">
              <span className="text-slate-500">Linha digitável: </span>
              <span className="font-mono-jb text-slate-300">{charge.boleto_digitable}</span>
            </div>
          )}
          {charge.qr_img && (
            <img src={charge.qr_img} alt="QR Code PIX" className="mx-auto w-48 h-48 rounded-md border border-slate-800" />
          )}
          <div className="flex items-center gap-2 min-w-0">
            <code className="flex-1 min-w-0 text-xs font-mono-jb text-emerald-300 bg-slate-950 border border-slate-800 rounded px-3 py-2 truncate">
              {charge.pix_copia_cola}
            </code>
            <Button
              type="button" size="sm" variant="outline"
              onClick={() => { navigator.clipboard.writeText(charge.pix_copia_cola); toast.success("Copia e cola copiado!"); }}
              className="border-slate-700 hover:bg-slate-800 hover:text-white shrink-0"
            >
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
          <Button
            type="button" size="sm"
            data-testid="result-send-now"
            onClick={() => onSend && onSend(charge._id)}
            disabled={sending}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            <Send className="w-3.5 h-3.5 mr-2" /> {sending ? "Enviando…" : "Enviar agora pro cliente"}
          </Button>
          <p className="text-xs text-slate-500">
            Os lembretes automáticos (antes, no e após o vencimento) serão enviados pro cliente seguindo o mesmo padrão das cobranças normais.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Edicao de cobranca avulsa (kind="charge") ou de recorrencia (kind="recurrence").
function EditDialog({ state, onClose, onSaved }) {
  const item = state?.item;
  const kind = state?.kind;
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [day, setDay] = useState("");
  const [dType, setDType] = useState("none");
  const [dValue, setDValue] = useState("");
  const [block, setBlock] = useState(false);
  const [boleto, setBoleto] = useState("no");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!item) return;
    setBoleto(item.has_boleto ? "yes" : "no");
    setDescription(item.description || "");
    setAmount(String(item.amount ?? ""));
    setDueDate(item.due_date || "");
    setDay(String(item.day_of_month ?? ""));
    setDType(item.discount_type || "none");
    setDValue(item.discount_value ? String(item.discount_value) : "");
    setBlock(!!(item.block_on_overdue ?? item.blocks_on_overdue));
  }, [item]);

  if (!item) return null;
  const isCharge = kind === "charge";
  const pixChanged = isCharge && (
    parseFloat(amount) !== parseFloat(item.amount) || dueDate !== item.due_date ||
    (dType === "none" ? "" : dType) !== (item.discount_type || "") ||
    (dType === "none" ? 0 : parseFloat(dValue) || 0) !== (item.discount_value || 0) ||
    (item.provider === "openpix" && (boleto === "yes") !== !!item.has_boleto)
  );

  const save = async () => {
    if (!amount || parseFloat(amount) <= 0) return toast.error("Informe um valor válido");
    if (isCharge && !dueDate) return toast.error("Informe o vencimento");
    if (!isCharge && (!day || day < 1 || day > 31)) return toast.error("Dia do vencimento de 1 a 31");
    if (dType !== "none" && (!dValue || parseFloat(dValue) <= 0)) return toast.error("Informe o valor do desconto ou escolha 'Sem desconto'");
    const body = {
      description,
      amount: parseFloat(amount),
      discount_type: dType,
      discount_value: dType === "none" ? null : parseFloat(dValue),
      block_on_overdue: block,
    };
    if (item.provider === "openpix") {
      if (boleto === "yes" && dType !== "none") return toast.error("Desconto ainda não está disponível em cobrança com boleto");
      body.with_boleto = boleto === "yes";
    }
    if (isCharge) body.due_date = dueDate; else body.day_of_month = parseInt(day, 10);
    setSaving(true);
    try {
      const url = isCharge ? `/manual-charges/${item._id}` : `/manual-charges/recurring/${item._id}`;
      const { data } = await api.put(url, body);
      toast.success(data.regenerated ? "Cobrança atualizada com um PIX novo. Use “Enviar” para mandar ao cliente." : "Alterações salvas.");
      onSaved();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const typeBtn = (v, label) => (
    <button
      type="button" onClick={() => { setDType(v); if (v === "none") setDValue(""); }}
      className={`px-3 py-2 rounded-md border text-sm transition-colors ${
        dType === v ? (v === "none" ? "bg-slate-800 border-slate-600 text-white" : "bg-emerald-500/10 border-emerald-500/40 text-emerald-300")
          : "bg-slate-950 border-slate-800 text-slate-400 hover:text-white"}`}
    >{label}</button>
  );

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg" data-testid="edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-emerald-400" /> {isCharge ? "Editar cobrança" : "Editar recorrência"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-slate-400">
            Cliente: <span className="text-white font-medium">{item.client_name}</span>{" "}
            <span className="font-mono-jb text-xs text-slate-500">{item.client_login}</span>
            <div className="text-xs text-slate-500 mt-0.5">O celular é lido do mk-auth a cada envio.</div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Valor (R$)</label>
              <Input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} data-testid="edit-amount" />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">{isCharge ? "Vencimento" : "Dia do vencimento (todo mês)"}</label>
              {isCharge
                ? <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} data-testid="edit-due" />
                : <Input type="number" min="1" max="31" value={day} onChange={(e) => setDay(e.target.value)} className={inputCls} data-testid="edit-day" />}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Descrição</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} data-testid="edit-description" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Desconto por antecipação</label>
            <div className="flex flex-wrap gap-2">
              {typeBtn("none", "Sem desconto")}
              {typeBtn("percent", "% Percentual")}
              {typeBtn("fixed", "R$ Valor fixo")}
              {dType !== "none" && (
                <Input type="number" step="0.01" min="0.01" value={dValue} onChange={(e) => setDValue(e.target.value)}
                       placeholder={dType === "percent" ? "Ex.: 5" : "Ex.: 10,00"} className={`${inputCls} w-28`} />
              )}
            </div>
          </div>
          {(
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Boleto</label>
              <select
                data-testid="edit-with-boleto"
                value={item.provider === "openpix" ? boleto : "no"}
                onChange={(e) => setBoleto(e.target.value)}
                disabled={item.provider !== "openpix"}
                className={`${inputCls} w-full rounded-md border px-3 py-2 text-sm disabled:opacity-50`}
              >
                <option value="no">Somente PIX</option>
                <option value="yes">PIX + boleto (PDF no WhatsApp)</option>
              </select>
              <p className="text-xs text-slate-600 mt-1.5">
                {item.provider !== "openpix"
                  ? "Boleto disponível apenas em cobranças da OpenPix."
                  : isCharge
                    ? "Exige CPF e endereço completo no mk-auth. Tarifa de R$ 2,99 por boleto. Não combina com desconto. Ao mudar, a cobrança é regerada."
                    : "Vale para as próximas cobranças geradas por esta recorrência (as já geradas não mudam). Exige CPF e endereço completo no mk-auth, tarifa de R$ 2,99 por boleto e não combina com desconto."}
              </p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button" onClick={() => setBlock((v) => !v)}
              className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${block ? "bg-red-500" : "bg-slate-700"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${block ? "translate-x-4" : ""}`} />
            </button>
            <span className="text-sm text-slate-300">Bloquear cliente se não pagar (10 dias de atraso)</span>
          </div>
          {pixChanged && (
            <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2" data-testid="edit-pix-warning">
              Valor, vencimento, desconto ou boleto mudaram: será gerada uma <b>cobrança nova</b> (PIX{boleto === "yes" ? " + boleto" : ""}) e a anterior será cancelada.
              Depois, use “Enviar” para o cliente receber o novo código.
            </div>
          )}
          {!isCharge && (
            <p className="text-xs text-slate-500">A mudança vale para as próximas cobranças geradas; as já geradas não mudam.</p>
          )}
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 border-slate-700 hover:bg-slate-800 text-white">Fechar</Button>
            <Button type="button" onClick={save} disabled={saving} data-testid="edit-save"
                    className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {saving ? "Salvando…" : "Salvar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ManualCharges() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const filtersRef = useRef(EMPTY_FILTERS); // le o filtro atual sem depender de re-render (load() e chamado logo apos mudar)

  const [client, setClient] = useState(null);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [provider, setProvider] = useState("efi");
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [blockOnOverdue, setBlockOnOverdue] = useState(false);
  const [withBoleto, setWithBoleto] = useState("no"); // "no" | "yes" - gera tambem boleto (OpenPix)
  const [dayOfMonth, setDayOfMonth] = useState("10");
  const [creating, setCreating] = useState(false);
  const [recurrences, setRecurrences] = useState([]);
  const [result, setResult] = useState(null);
  const [editState, setEditState] = useState(null); // { kind: "charge"|"recurrence", item }

  const loadSummary = async () => {
    try {
      const { data } = await api.get("/manual-charges/summary");
      setSummary(data);
    } catch {
      // resumo e complementar - a tela funciona sem ele
    }
  };

  const load = async (q = search, p = page, f = filtersRef.current) => {
    try {
      const params = { search: q, page: p, limit: PAGE_SIZE };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/manual-charges", { params });
      setData(data);
      setPage(data.page || 1);
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
    loadSummary();
  };

  const changeFilter = (patch) => {
    const next = { ...filtersRef.current, ...patch };
    filtersRef.current = next;
    setFilters(next);
    load(search, 1, next);
  };

  const clearFilters = () => {
    filtersRef.current = EMPTY_FILTERS;
    setFilters(EMPTY_FILTERS);
    load(search, 1, EMPTY_FILTERS);
  };

  const hasFilters = Object.values(filters).some(Boolean);

  const loadRecurrences = async () => {
    try {
      const { data } = await api.get("/manual-charges/recurring");
      setRecurrences((data.recurrences || []).filter((r) => r.active));
    } catch {
      // falha silenciosa - nao e critico pra tela funcionar
    }
  };

  useEffect(() => { load("", 1); loadRecurrences(); /* eslint-disable-next-line */ }, []);

  const cancelCharge = async (id) => {
    try {
      await api.post(`/manual-charges/${id}/cancel`);
      toast.success("Cobrança cancelada.");
      load(search, data?.page || 1);
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const [sendingChargeId, setSendingChargeId] = useState(null);
  const sendChargeNow = async (id) => {
    setSendingChargeId(id);
    try {
      const { data } = await api.post(`/manual-charges/${id}/send-now`);
      if (data.sent) {
        toast.success("Cobrança enviada para o cliente!");
      } else {
        toast.error(`Não enviada: ${data.reason || "motivo desconhecido"}`);
      }
      load(search, data?.page || 1);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSendingChargeId(null);
    }
  };

  const [cancelDialog, setCancelDialog] = useState(null); // { recurrence, openCharges }
  const openCancelRecurrenceDialog = async (rec) => {
    try {
      const { data } = await api.get(`/manual-charges/recurring/${rec._id}/charges`);
      setCancelDialog({ recurrence: rec, openCharges: data.charges || [] });
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const confirmCancelRecurrence = async (alsoCancelCharges) => {
    const rec = cancelDialog.recurrence;
    try {
      await api.delete(`/manual-charges/recurring/${rec._id}`, { params: { cancel_charges: alsoCancelCharges } });
      toast.success(
        alsoCancelCharges
          ? "Recorrência e cobrança(s) em aberto canceladas."
          : "Recorrência cancelada. As cobranças já geradas continuam em aberto."
      );
      setCancelDialog(null);
      loadRecurrences();
      load(search, data?.page || 1);
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  const [sendingId, setSendingId] = useState(null);
  const sendRecurrenceNow = async (id) => {
    setSendingId(id);
    try {
      const { data } = await api.post(`/manual-charges/recurring/${id}/send-now`);
      if (data.sent) {
        toast.success("Cobrança enviada para o cliente agora!");
      } else {
        toast.error(`Cobrança gerada, mas não enviada: ${data.reason || "motivo desconhecido"}`);
      }
      loadRecurrences();
      load("", 1);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSendingId(null);
    }
  };

  const create = async () => {
    if (!client) return toast.error("Selecione um cliente primeiro");
    if (!amount || parseFloat(amount) <= 0) return toast.error("Informe um valor válido");
    if (!isRecurring && !dueDate) return toast.error("Informe a data de vencimento");
    if (isRecurring && (!dayOfMonth || dayOfMonth < 1 || dayOfMonth > 31)) {
      return toast.error("Informe o dia do vencimento (1 a 31)");
    }
    if (discountType !== "none" && (!discountValue || parseFloat(discountValue) <= 0)) {
      return toast.error("Informe o valor do desconto, ou selecione 'Sem desconto'");
    }
    const boletoOn = withBoleto === "yes" && provider === "openpix";
    if (boletoOn && discountType !== "none") {
      return toast.error("Desconto ainda não está disponível em cobrança com boleto");
    }

    setCreating(true);
    try {
      const discountFields = {
        discount_type: discountType === "none" ? null : discountType,
        discount_value: discountType === "none" ? null : parseFloat(discountValue),
      };
      if (isRecurring) {
        await api.post("/manual-charges/recurring", {
          client_login: client.login,
          description,
          amount: parseFloat(amount),
          provider,
          day_of_month: parseInt(dayOfMonth, 10),
          block_on_overdue: blockOnOverdue,
          with_boleto: boletoOn,
          ...discountFields,
        });
        toast.success("Recorrência criada! A primeira cobrança sai automaticamente quando faltar 10 dias para o vencimento.");
        loadRecurrences();
      } else {
        const { data } = await api.post("/manual-charges", {
          client_login: client.login,
          description,
          amount: parseFloat(amount),
          due_date: dueDate,
          provider,
          block_on_overdue: blockOnOverdue,
          with_boleto: boletoOn,
          ...discountFields,
        });
        setResult(data);
        toast.success(boletoOn ? "Cobrança avulsa criada, com boleto!" : "Cobrança avulsa criada!");
      }
      setClient(null);
      setDescription("");
      setAmount("");
      setDueDate("");
      setDiscountType("none");
      setDiscountValue("");
      setIsRecurring(false);
      setDayOfMonth("10");
      setBlockOnOverdue(false);
      setWithBoleto("no");
      load("", 1);
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setCreating(false);
    }
  };

  const charges = data?.charges || [];

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-2">
        <Receipt className="w-5 h-5 text-emerald-400" />
        <h1 className="font-outfit text-2xl font-bold text-white">Cobrança Avulsa</h1>
      </div>
      <p className="text-sm text-slate-500">
        Cobranças PIX criadas na hora, pra um cliente específico — sem relação com as faturas do mk-auth, com o
        banco (EFI ou OpenPix) escolhido manualmente a cada cobrança.
      </p>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="avulsa-summary">
          <SummaryCard
            testId="summary-open" label="Em aberto" tone="amber" icon={Clock}
            value={fmtMoney(summary.open.total)}
            sub={`${summary.open.count} ${plural(summary.open.count, "cobrança", "cobranças")} no prazo`}
            onClick={() => changeFilter({ status: "ABERTA" })}
          />
          <SummaryCard
            testId="summary-overdue" label="Vencidas" tone={summary.overdue.count > 0 ? "red" : "slate"} icon={AlertTriangle}
            value={fmtMoney(summary.overdue.total)}
            sub={`${summary.overdue.count} ${plural(summary.overdue.count, "cobrança", "cobranças")} em atraso`}
            onClick={() => changeFilter({ status: "VENCIDA" })}
          />
          <SummaryCard
            testId="summary-paid" label="Recebido no mês" tone="emerald" icon={CheckCircle2}
            value={fmtMoney(summary.paid_month.total)}
            sub={`${summary.paid_month.count} ${plural(summary.paid_month.count, "cobrança paga", "cobranças pagas")}`}
            onClick={() => changeFilter({ status: "PAGA" })}
          />
          <SummaryCard
            testId="summary-blocked" label="Bloqueio" tone={summary.blocked_clients > 0 ? "red" : "slate"} icon={Lock}
            value={`${summary.blocked_clients} ${plural(summary.blocked_clients, "bloqueado", "bloqueados")}`}
            sub={`${summary.at_risk_clients} ${plural(summary.at_risk_clients, "cliente em risco", "clientes em risco")} de bloqueio`}
          />
          <SummaryCard
            testId="summary-recurring" label="Recorrências" tone="sky" icon={Repeat}
            value={`${summary.recurrences.count} ${plural(summary.recurrences.count, "ativa", "ativas")}`}
            sub={`≈ ${fmtMoney(summary.recurrences.monthly_total)} por mês`}
          />
        </div>
      )}

      <Card className="bg-slate-900/50 border-slate-800 p-6 space-y-4">
        <div className="text-sm text-slate-300 font-medium">Nova cobrança</div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500 mb-1.5 block">Cliente</label>
            <ClientPicker value={client} onChange={setClient} />
          </div>

          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Valor (R$)</label>
            <Input
              data-testid="avulsa-amount-input"
              type="number" step="0.01" min="0.01"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">
              {isRecurring ? "Dia do vencimento (todo mês)" : "Vencimento"}
            </label>
            {isRecurring ? (
              <Input
                data-testid="avulsa-day-of-month-input"
                type="number" min="1" max="31"
                value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)}
                placeholder="Ex.: 10"
                className={inputCls}
              />
            ) : null}
            {isRecurring && (
              <p className="text-xs text-slate-600 mt-1.5">
                Em meses mais curtos (ex.: fevereiro), ajusta automaticamente para o último dia do mês.
              </p>
            )}
            {!isRecurring && (
              <Input
                data-testid="avulsa-due-date-input"
                type="date"
                value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                className={inputCls}
              />
            )}
          </div>

          <div className="sm:col-span-2 flex items-center gap-2 -mt-1">
            <button
              type="button"
              data-testid="avulsa-recurring-toggle"
              onClick={() => setIsRecurring((v) => !v)}
              className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${isRecurring ? "bg-emerald-500" : "bg-slate-700"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${isRecurring ? "translate-x-4" : ""}`} />
            </button>
            <span className="text-sm text-slate-300">Cobrança recorrente (gera automaticamente todo mês)</span>
          </div>

          <div className="sm:col-span-2 flex items-center gap-2">
            <button
              type="button"
              data-testid="avulsa-block-toggle"
              onClick={() => setBlockOnOverdue((v) => !v)}
              className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${blockOnOverdue ? "bg-red-500" : "bg-slate-700"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${blockOnOverdue ? "translate-x-4" : ""}`} />
            </button>
            <span className="text-sm text-slate-300">
              Bloquear cliente se não pagar (10 dias de atraso)
              <span className="text-slate-500"> — desligado por padrão</span>
            </span>
          </div>

          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500 mb-1.5 block">Descrição (opcional)</label>
            <Input
              data-testid="avulsa-description-input"
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex.: Taxa de instalação, visita técnica…"
              className={inputCls}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500 mb-1.5 block">Desconto por antecipação (opcional)</label>
            <div className="flex gap-2">
              <button
                type="button" data-testid="discount-type-none"
                onClick={() => { setDiscountType("none"); setDiscountValue(""); }}
                className={`px-3 py-2 rounded-md border text-sm transition-colors ${
                  discountType === "none"
                    ? "bg-slate-800 border-slate-600 text-white"
                    : "bg-slate-950 border-slate-800 text-slate-400 hover:text-white"
                }`}
              >
                Sem desconto
              </button>
              <button
                type="button" data-testid="discount-type-percent"
                onClick={() => setDiscountType("percent")}
                className={`px-3 py-2 rounded-md border text-sm transition-colors ${
                  discountType === "percent"
                    ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                    : "bg-slate-950 border-slate-800 text-slate-400 hover:text-white"
                }`}
              >
                % Percentual
              </button>
              <button
                type="button" data-testid="discount-type-fixed"
                onClick={() => setDiscountType("fixed")}
                className={`px-3 py-2 rounded-md border text-sm transition-colors ${
                  discountType === "fixed"
                    ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                    : "bg-slate-950 border-slate-800 text-slate-400 hover:text-white"
                }`}
              >
                R$ Valor fixo
              </button>
              {discountType !== "none" && (
                <Input
                  data-testid="discount-value-input"
                  type="number" step="0.01" min="0.01"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={discountType === "percent" ? "Ex.: 5" : "Ex.: 10,00"}
                  className={`${inputCls} w-32`}
                />
              )}
            </div>
            <p className="text-xs text-slate-600 mt-1.5">Válido se o cliente pagar até a data de vencimento.</p>
          </div>

          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500 mb-1.5 block">Banco (PIX)</label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="avulsa-provider-efi"
                onClick={() => setProvider("efi")}
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                  provider === "efi"
                    ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                    : "bg-slate-950 border-slate-800 text-slate-400 hover:text-white"
                }`}
              >
                EFI
              </button>
              <button
                type="button"
                data-testid="avulsa-provider-openpix"
                onClick={() => setProvider("openpix")}
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                  provider === "openpix"
                    ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                    : "bg-slate-950 border-slate-800 text-slate-400 hover:text-white"
                }`}
              >
                OpenPix
              </button>
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500 mb-1.5 block">Boleto</label>
            <select
              data-testid="avulsa-with-boleto"
              value={provider === "openpix" ? withBoleto : "no"}
              onChange={(e) => setWithBoleto(e.target.value)}
              disabled={provider !== "openpix"}
              className={`${inputCls} w-full rounded-md border px-3 py-2 text-sm disabled:opacity-50`}
            >
              <option value="no">Somente PIX</option>
              <option value="yes">PIX + boleto (PDF no WhatsApp)</option>
            </select>
            <p className="text-xs text-slate-600 mt-1.5">
              {provider !== "openpix"
                ? "Boleto disponível apenas com a OpenPix."
                : withBoleto === "yes"
                  ? "Exige CPF e endereço completo no mk-auth. Tarifa de R$ 2,99 por boleto emitido. Não combina com desconto."
                  : "O boleto é emitido junto com o PIX, na mesma cobrança."}
            </p>
          </div>
        </div>

        <Button
          data-testid="avulsa-create-btn"
          onClick={create}
          disabled={creating}
          className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
        >
          <Plus className="w-4 h-4 mr-2" />
          {creating ? "Salvando…" : isRecurring ? "Criar recorrência" : "Criar cobrança"}
        </Button>
      </Card>

      <Card className="bg-slate-900/50 border-slate-800 p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
            Cobranças avulsas {data && `· ${data.total} ${hasFilters || search.trim() ? plural(data.total, "encontrada", "encontradas") : "no total"}`}
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="avulsa-search-input"
              placeholder="Buscar por cliente, descrição…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(search, 1)}
              className={`${inputCls} h-9 text-sm w-56`}
            />
            <Button size="sm" onClick={() => load(search, 1)} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold h-9">
              <Search className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2 mb-4" data-testid="avulsa-filters">
          <FilterSelect
            label="Status" testId="avulsa-filter-status" value={filters.status}
            onChange={(v) => changeFilter({ status: v })}
            options={[["", "Todos"], ["ABERTA", "Em aberto (no prazo)"], ["VENCIDA", "Vencidas"], ["PAGA", "Pagas"], ["CANCELADA", "Canceladas"]]}
          />
          <FilterSelect
            label="Banco" testId="avulsa-filter-provider" value={filters.provider}
            onChange={(v) => changeFilter({ provider: v })}
            options={[["", "Todos"], ["efi", "EFI"], ["openpix", "OpenPix"]]}
          />
          <FilterSelect
            label="Bloqueio" testId="avulsa-filter-blocking" value={filters.blocking}
            onChange={(v) => changeFilter({ blocking: v })}
            options={[["", "Todos"], ["yes", "Bloqueia se atrasar"], ["no", "Não bloqueia"]]}
          />
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1 block">Vence de</label>
            <Input
              data-testid="avulsa-filter-due-from" type="date" value={filters.due_from}
              onChange={(e) => changeFilter({ due_from: e.target.value })}
              className={`${inputCls} h-9 text-sm w-36`}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1 block">até</label>
            <Input
              data-testid="avulsa-filter-due-to" type="date" value={filters.due_to}
              onChange={(e) => changeFilter({ due_to: e.target.value })}
              className={`${inputCls} h-9 text-sm w-36`}
            />
          </div>
          {hasFilters && (
            <Button
              type="button" size="sm" variant="outline" data-testid="avulsa-filter-clear"
              onClick={clearFilters}
              className="border-slate-700 hover:bg-slate-800 text-slate-300 h-9 text-xs"
            >
              Limpar filtros
            </Button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                <th className="py-2 pr-3 min-w-[190px]">Cliente</th>
                <th className="py-2 pr-3 min-w-[150px]">Descrição</th>
                <th className="py-2 pr-3 whitespace-nowrap">Valor</th>
                <th className="py-2 pr-3 whitespace-nowrap">Vencimento</th>
                <th className="py-2 pr-3">Banco</th>
                <th className="py-2 pr-3 min-w-[110px]">Status</th>
                <th className="py-2 w-px"></th>
              </tr>
            </thead>
            <tbody>
              {charges.map((c) => {
                const badge = (c.is_overdue ? OVERDUE_BADGE : STATUS_BADGE[c.status]) ||
                  { label: c.status, className: "bg-slate-800 text-slate-400 border-slate-700" };
                const hint = dueHint(c);
                const extras = chargeExtras(c);
                const sentReminders = REMINDER_LABELS.filter(([k]) => c.reminders_sent && c.reminders_sent[k]).map(([, l]) => l);
                return (
                  <tr key={c._id} className="border-b border-slate-800/60 align-top">
                    <td className="py-2.5 pr-3">
                      <div className="text-white">{c.client_name}</div>
                      <div className="text-xs font-mono-jb text-slate-500">{c.client_login}</div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                        {c.recurrence_id && (
                          <span title="Gerada por uma recorrência" className="inline-flex items-center gap-1 text-[10px] text-sky-400">
                            <Repeat className="w-3 h-3" /> Recorrente
                          </span>
                        )}
                        {c.blocks_on_overdue && (
                          <span title="O cliente será bloqueado se não pagar (10 dias de atraso)" className="inline-flex items-center gap-1 text-[10px] text-slate-400">
                            <Lock className="w-3 h-3" /> Bloqueia se atrasar
                          </span>
                        )}
                        {c.client_blocked && (
                          <span title="Cliente já bloqueado por cobrança avulsa" className="inline-flex items-center gap-1 text-[10px] text-red-400 font-medium">
                            <AlertTriangle className="w-3 h-3" /> Cliente bloqueado
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-400">
                      <div>{c.description || "—"}</div>
                      {extras.length > 0 && <div className="text-[10px] text-slate-500 mt-1">{extras.join(" · ")}</div>}
                    </td>
                    <td className="py-2.5 pr-3 font-mono-jb text-slate-300 whitespace-nowrap">{fmtMoney(c.amount)}</td>
                    <td className="py-2.5 pr-3 text-slate-400 whitespace-nowrap">
                      <div>{fmtDate(c.due_date)}</div>
                      {hint && <div className={`text-[11px] ${hint.className}`}>{hint.text}</div>}
                    </td>
                    <td className="py-2.5 pr-3">
                      <Badge className="bg-slate-800 text-slate-300 border-slate-700 uppercase text-[10px]">{c.provider}</Badge>
                      {c.has_boleto && (
                        <Badge data-testid={`boleto-badge-${c._id}`} className="ml-1 bg-sky-500/10 text-sky-300 border-sky-500/30 uppercase text-[10px]">+ boleto</Badge>
                      )}
                    </td>
                    <td className="py-2.5 pr-3">
                      <Badge className={`${badge.className} text-[10px]`}>{badge.label}</Badge>
                      {c.status === "PAGA" && c.paid_amount && Number(c.paid_amount) !== Number(c.amount) && (
                        <div className="text-[11px] text-emerald-300 mt-1" data-testid={`paid-amount-${c._id}`}>
                          pago {fmtMoney(c.paid_amount)}
                        </div>
                      )}
                      {c.status === "PAGA" && c.paid_at && (
                        <div className="text-[11px] text-emerald-400/80 mt-1">em {fmtDate(c.paid_at)}</div>
                      )}
                      {c.status === "ATIVA" && (
                        <div className="text-[10px] text-slate-500 mt-1">
                          {sentReminders.length > 0 ? `Lembretes: ${sentReminders.join(" · ")}` : "Nenhum lembrete enviado"}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      {c.status === "ATIVA" && (
                        <div className="inline-flex items-center gap-1">
                          <Button
                            type="button" size="sm" variant="outline" title="Enviar a cobrança agora pelo WhatsApp"
                            data-testid={`send-charge-${c._id}`}
                            onClick={() => sendChargeNow(c._id)}
                            disabled={sendingChargeId === c._id}
                            className="h-8 px-2.5 border-slate-700 hover:bg-emerald-500/10 hover:border-emerald-500/40 hover:text-emerald-300 text-xs"
                          >
                            <Send className="w-3.5 h-3.5 mr-1.5" />{sendingChargeId === c._id ? "Enviando…" : "Enviar"}
                          </Button>
                          <Button
                            type="button" size="sm" variant="outline" title="Editar cobrança"
                            data-testid={`edit-charge-${c._id}`}
                            onClick={() => setEditState({ kind: "charge", item: c })}
                            className="h-8 px-2.5 border-slate-700 hover:bg-slate-800 hover:text-white text-xs"
                          >
                            <Pencil className="w-3.5 h-3.5 mr-1.5" />Editar
                          </Button>
                          <Button
                            type="button" size="sm" variant="outline" title="Cancelar cobrança"
                            data-testid={`cancel-charge-${c._id}`}
                            onClick={() => cancelCharge(c._id)}
                            className="h-8 px-2.5 border-slate-700 hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-300 text-xs"
                          >
                            <Ban className="w-3.5 h-3.5 mr-1.5" />Cancelar
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {charges.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-6 text-center text-slate-500">
                    {search.trim() || hasFilters ? "Nenhuma cobrança encontrada com esses filtros." : "Nenhuma cobrança avulsa criada ainda."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {data && data.total > 0 && (
          <div className="flex items-center justify-between pt-4 mt-2 border-t border-slate-800 flex-wrap gap-2">
            <div className="text-xs text-slate-500">
              Página {data.page} de {data.pages} · {data.total} cobrança(s) no total
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => load(search, 1)} disabled={data.page === 1} className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button onClick={() => load(search, Math.max(1, data.page - 1))} disabled={data.page === 1} className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-slate-400 px-2 font-mono-jb">{data.page} / {data.pages}</span>
              <button onClick={() => load(search, Math.min(data.pages, data.page + 1))} disabled={data.page === data.pages} className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronRight className="w-4 h-4" />
              </button>
              <button onClick={() => load(search, data.pages)} disabled={data.page === data.pages} className="text-slate-400 hover:text-emerald-300 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </Card>

      {recurrences.length > 0 && (
        <Card className="bg-slate-900/50 border-slate-800 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-3">
            Recorrências ativas · {recurrences.length}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                  <th className="py-2 pr-3 min-w-[190px]">Cliente</th>
                  <th className="py-2 pr-3 min-w-[150px]">Descrição</th>
                  <th className="py-2 pr-3 whitespace-nowrap">Valor</th>
                  <th className="py-2 pr-3 whitespace-nowrap">Vencimento</th>
                  <th className="py-2 pr-3">Banco</th>
                  <th className="py-2 pr-3 min-w-[110px]">Status</th>
                  <th className="py-2 w-px"></th>
                </tr>
              </thead>
              <tbody>
                {recurrences.map((r) => (
                  <tr key={r._id} className="border-b border-slate-800/60">
                    <td className="py-2.5 pr-3">
                      <div className="text-white">{r.client_name}</div>
                      <div className="text-xs font-mono-jb text-slate-500">{r.client_login}</div>
                    </td>
                    <td className="py-2.5 pr-3 text-slate-400">{r.description || "—"}</td>
                    <td className="py-2.5 pr-3 font-mono-jb text-slate-300 whitespace-nowrap">{fmtMoney(r.amount)}</td>
                    <td className="py-2.5 pr-3 text-slate-400 whitespace-nowrap">
                      {fmtDate(r.next_due_date)}
                      <span className="text-slate-600"> (todo dia {r.day_of_month})</span>
                    </td>
                    <td className="py-2.5 pr-3">
                      <Badge className="bg-slate-800 text-slate-300 border-slate-700 uppercase text-[10px]">{r.provider}</Badge>
                      {r.has_boleto && (
                        <Badge className="ml-1 bg-sky-500/10 text-sky-300 border-sky-500/30 uppercase text-[10px]">+ boleto</Badge>
                      )}
                    </td>
                    <td className="py-2.5 pr-3">
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-[10px]">
                        Ativa · {r.charges_generated} gerada(s)
                      </Badge>
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          type="button" size="sm" variant="outline" title="Gerar e enviar a cobrança do ciclo agora"
                          data-testid={`send-recurrence-${r._id}`}
                          onClick={() => sendRecurrenceNow(r._id)}
                          disabled={sendingId === r._id}
                          className="h-8 px-2.5 border-slate-700 hover:bg-emerald-500/10 hover:border-emerald-500/40 hover:text-emerald-300 text-xs"
                        >
                          <Send className="w-3.5 h-3.5 mr-1.5" />{sendingId === r._id ? "Enviando…" : "Enviar"}
                        </Button>
                        <Button
                          type="button" size="sm" variant="outline" title="Editar recorrência"
                          data-testid={`edit-recurrence-${r._id}`}
                          onClick={() => setEditState({ kind: "recurrence", item: r })}
                          className="h-8 px-2.5 border-slate-700 hover:bg-slate-800 hover:text-white text-xs"
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1.5" />Editar
                        </Button>
                        <Button
                          type="button" size="sm" variant="outline" title="Cancelar recorrência"
                          data-testid={`cancel-recurrence-${r._id}`}
                          onClick={() => openCancelRecurrenceDialog(r)}
                          className="h-8 px-2.5 border-slate-700 hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-300 text-xs"
                        >
                          <Ban className="w-3.5 h-3.5 mr-1.5" />Cancelar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <EditDialog
        state={editState}
        onClose={() => setEditState(null)}
        onSaved={() => { setEditState(null); loadRecurrences(); load(search, data?.page || 1); }}
      />
      <ResultDialog charge={result} onClose={() => setResult(null)} onSend={sendChargeNow} sending={sendingChargeId === result?._id} />
      <CancelRecurrenceDialog
        state={cancelDialog}
        onClose={() => setCancelDialog(null)}
        onConfirm={confirmCancelRecurrence}
      />
    </div>
  );
}
